import { FC, useState } from 'react'
import styled from '@emotion/styled'
import { ChevronSmallLeft } from '@emotion-icons/entypo/ChevronSmallLeft'

import { Bounds, MapStyle } from 'src/types/map'
import { MapInner } from 'src/components/map/mapbox-main-map/map-component'
import { MAP_DEFAULT_STYLE } from 'src/components/map/mapbox/config'
import { MapTypeToggle } from 'src/components/map/google-map/map-type-toggle'
import { StatesLayer } from 'src/v2/components/activity-heatmap-map/states-layer'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { CONTINENTAL_US_BOUNDS } from 'src/v2/components/activity-heatmap-map/constants'
import { Colors, Shapes } from 'src/components/theme'
import { PropertiesLayer } from 'src/v2/components/activity-heatmap-map/properties-layer'
import { Legend } from 'src/components/states-layer/legend'
import { useAtomValue } from 'jotai'
import { activityHeatmapFeaturesById } from 'src/v2/state/owner-reveal/activity-heatmap-map'
import { WidgetCardEmpty } from 'src/v2/components/widget-card/empty'

const MapContainer = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  height: 520px;
  border-radius: ${Shapes.borderRadius}px;
  overflow: hidden;
`

const MapHeaderContainer = styled.div`
  padding: 14px;
`

type ActivityHeatmapMapProps = {
  entityId: string
  stateCode?: string
  onClearSelection?: () => void
  onStateCodeChange?: (stateCode: string) => void
}

export const ActivityHeatmapMap: FC<ActivityHeatmapMapProps> = ({
  entityId,
  stateCode,
  onClearSelection,
  onStateCodeChange,
}) => {
  // Center of the US
  const [bounds, setBounds] = useState<Bounds>(CONTINENTAL_US_BOUNDS)
  const [zoom, setZoom] = useState(3)
  const [style, setStyle] = useState<MapStyle>(MAP_DEFAULT_STYLE)
  const features = useAtomValue(activityHeatmapFeaturesById(entityId))

  const clearSelectedState = () => onClearSelection?.()

  if (features.length === 0) {
    return <WidgetCardEmpty style={{ padding: 0 }} message="No recent activity to show for this entity" />
  }

  return (
    <MapContainer>
      <MapInner zoom={zoom} onZoomChange={setZoom} bounds={bounds} onBoundsChange={setBounds} style={style}>
        <MapHeaderContainer>
          {stateCode && (
            <ButtonWithIcon onClick={clearSelectedState} ghost>
              <ChevronSmallLeft size={16} /> Back to Main Map
            </ButtonWithIcon>
          )}
        </MapHeaderContainer>
        <PropertiesLayer entityId={entityId} stateCode={stateCode} />
        <StatesLayer entityId={entityId} onStateCodeChange={onStateCodeChange} stateCode={stateCode} />
        {!stateCode && <Legend color={Colors.primaryRed700} />}
        <MapTypeToggle zoom={zoom} mapStyle={style} setMapStyle={setStyle} />
      </MapInner>
    </MapContainer>
  )
}
